import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { useSearchParams } from "react-router-dom";

import AllProducts from "./Tabs/Store/AllProducts";
import GrowthCloudStoreNavbar from "./Navbars/GrowthCloudStoreNavbar";
import GrowthCloudStoreHeader from "./Headers/GrowthCloudStoreHeader";
import MyLocalApps from "./Tabs/Store/MyLocalApps";
import InstalledApps from "./Tabs/Store/InstalledApps";
import AppDrawer from "../Drawers/AppDrawer";
import Search from "../PageView/PageViewTable/Search";
import CloudAppDrawer from "../Drawers/CloudAppDrawer";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import AppDetailDrawer from "./Drawers/AppDetailDrawer";
import useGrowthCloudStore from "../../hooks/useGrowthCloudStore";

const MarketplaceTabs = ["all-products", "installed-apps", "local-apps"];

/**
 * MarketPlace Drawer
 * Stores marketplace for Cloud Apps & Global Apps
 */
const GrowthCloudStore = () => {
  const tenantID = useAtomValue(tenantIDAtom);
  const [params, setParams] = useSearchParams();
  const open = !!params?.get("store");
  const activeTab = params?.get("page") || "installed-apps";
  const [opened, setOpened] = useState(false);
  const { cPermissions } = useCloudPermissions();
  const isAdmin = cPermissions.cloud_modify_permissions;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const setActiveTab = (tab: string) => {
    params.set("page", tab);
    setParams(params);
  };
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };
  const onClose = () => setParams(new URLSearchParams());
  const openApp = (appID: string, scope: "global" | "local") => {
    params.set("app", appID);
    params.set("scope", scope);
    setParams(params);
  };

  const [createAppDrawer, setCreateAppDrawer] = useState(false);
  const {
    loading,
    extendedCloudApps,
    extendedGlobalApps,
    allInstalledApps,
    pendingUpdateApps,
    mostPopularApps,
    allMSTags,
  } = useGrowthCloudStore({ disabled: !open, searchTerm, selectedTags });

  useEffect(() => {
    setSelectedTags([]);
  }, [activeTab]);

  return (
    <AppDrawer
      open={open}
      onEntered={() => setOpened(true)}
      onExited={() => setOpened(false)}
      onClose={onClose}
      headerTitle="GrowthCloud Store"
      full
      bodyCSS="tw:px-0 tw:pt-0 tw:pb-20 tw:m-0"
      disablePadding
      headerOptions={
        <Search alwaysOpen onChange={(value) => handleSearch(value)} />
      }
      body={
        <>
          <div className="tw:flex tw:flex-col">
            <GrowthCloudStoreNavbar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            {activeTab === "all-products" && <GrowthCloudStoreHeader />}
            <div className="tw:w-full tw:h-full">
              {(activeTab === "all-products" ||
                !MarketplaceTabs.includes(activeTab)) && (
                <AllProducts
                  allApps={extendedGlobalApps}
                  mostPopularApps={mostPopularApps}
                  cloudApps={extendedCloudApps?.length ? extendedCloudApps : []}
                  loading={loading}
                  openApp={(id) => openApp(id, "global")}
                  mstags={allMSTags}
                  onTagsChange={setSelectedTags}
                  selectedTags={selectedTags}
                />
              )}
              {activeTab === "installed-apps" && (
                <InstalledApps
                  allInstalledApps={allInstalledApps}
                  pendingUpdateApps={pendingUpdateApps}
                  openApp={(id) => openApp(id, "global")}
                />
              )}
              {activeTab === "local-apps" && (
                <MyLocalApps
                  cloudApps={extendedCloudApps}
                  tenantID={tenantID}
                  isAdmin={isAdmin}
                  openApp={(id) => openApp(id, "local")}
                />
              )}
            </div>
          </div>
          <CloudAppDrawer
            open={createAppDrawer}
            onClose={() => setCreateAppDrawer(false)}
            type="Create"
          />

          <AppDetailDrawer
            appID={opened && params.get("app") ? params.get("app") : null}
            onClose={() => {
              params.delete("app");
              setParams(params);
            }}
            // isGlobalApp={params.get("scope") === "global"}
            isAdmin={isAdmin}
          />
        </>
      }
    />
  );
};

export default GrowthCloudStore;
